<script setup>
import { onMounted, computed, ref, watch, nextTick } from "vue";
import { useBookingStore } from "@stores/BookingStore";
import { useField, useForm } from "vee-validate";
import { useRouter } from "vue-router";

import FieldSearch from "@components/FieldSearch.vue";
import FieldLocation from "@components/FieldLocation.vue";
import FieldDatePicker from "@components/FieldDatePicker.vue";
import FieldAddPatients from "@components/FieldAddPatients.vue";
import ModalConsultationInfo from "@components/ModalConsultationInfo.vue";
import Dialog from "primevue/dialog";
import {
  formatDate,
  serializeQueryParams,
  parseQueryString,
} from "@app/helpers";

const props = defineProps({
  type: {
    type: String,
    default: "vaccinations",
  },
  style: {
    type: String,
    required: false,
  },
});

const { values, meta, validate } = useForm();
const bookingStore = useBookingStore();
const router = useRouter();

try {
  await bookingStore.getVaccinations();
} catch (error) {
  console.error("Failed to get vaccinations:", error);
}

try {
  await bookingStore.getDestinations();
} catch (error) {
  console.error("Failed to get destinations:", error);
}

const dates = ref();
const isOpen = ref(true);
const infoModalVisible = ref(false);

const toggleFinder = () => {
  isOpen.value = !isOpen.value;
};

const search = () => {
  // Validate user input
  validate();

  // Check if the input is valid
  if (meta.value.valid) {
    bookingStore.clearBookingDetails();

    // Update the booking store's query service type
    bookingStore.query.serviceType = bookingStore.serviceType;

    // Serialize the query parameters
    const serializedQuery = serializeQueryParams(bookingStore.query);

    // Convert serialized data to an encoded query string
    const queryString = new URLSearchParams(serializedQuery).toString();

    // Construct the target URL
    const targetUrl = `/app/booking/?${queryString}`;

    // Navigate to the target URL
    window.location.href = targetUrl;
  }
};

watch(
  () => bookingStore.query,
  () => {
    const serializedObj = JSON.stringify(bookingStore.query);
    sessionStorage.setItem("query", serializedObj);
  },
  { deep: true }
);

function onDateSelect(selectedDates) {
  // bookingStore.query.dates = selectedDates;
  // bookingStore.query.dates = selectedDates.map((item) => {
  //   return item ? formatDate(item, "yyyy-mm-dd") : null;
  // });
}
// onMounted(async () => {
//   await nextTick(() => {
//     document.body.classList.add("hero-mounted");
//   });
// });
</script>

<template>
  <section
    class="sm:flex sm:flex-col justify-center bg-white md:bg-offwhite shadow-xs px-20 lg:px-30 py-50 border border-grey-border rounded-3xl sm:min-h-[200px] text-neutral-600">
    <section class="sm:hidden flex items-start w-full">
      <div :class="{ 'mb-20': isOpen }">
        <div v-if="!isOpen">
          <span
            v-if="bookingStore.serviceType === 'vaccinations'"
            v-for="(vacc, index) in bookingStore.query.vaccinations"
            :key="vacc.id">
            {{ vacc.name }}
            <span v-if="index < bookingStore.query.vaccinations.length - 1">
              ,
            </span>
          </span>

          <p v-if="bookingStore.query.patients" class="text-13 text-grey/[0.7]">
            {{ bookingStore.query.patients.totalAdults }} Adult{{
              bookingStore.query.patients.totalAdults !== 1 ? "s" : ""
            }}, {{ bookingStore.query.patients.totalChildren }} Child{{
              bookingStore.query.patients.totalChildren !== 1 ? "ren" : ""
            }}
          </p>
          <p v-if="bookingStore.query.location" class="text-13 text-grey/[0.7]">
            {{ bookingStore.query.location }}
          </p>
        </div>
      </div>
      <button @click="toggleFinder" class="ml-auto">
        <i class="text-18 text-blue ri-pencil-fill"></i>
      </button>
    </section>

    <div
      v-if="type === 'consultations' && isOpen"
      class="mb-30 font-medium text-14 text-blue leading-[0] cursor-pointer r"
      @click="infoModalVisible = true">
      What is a travel consultation?
    </div>

    <form
      v-if="isOpen"
      action=""
      class="relative xl:flex max-xl:flex-col gap-3 grid sm:grid-cols-2 lg:grid-cols-3 w-full">
      <FieldSearch
        v-if="type === 'vaccinations'"
        class="grow"
        name="vaccinations"
        label="Vaccinations"
        errorPosition="absolute"
        v-model="bookingStore.query.vaccinations"
        :data="bookingStore.vaccinations"
        optionLabel="name"
        optionValue="id"
        placeholder="Search Vaccinations"
        required
        :showRequired="false"
        multiple />

      <FieldSearch
        v-if="type === 'consultations'"
        class="grow"
        name="search_destinations"
        label="Destination"
        errorPosition="absolute"
        v-model="bookingStore.query.destination"
        :data="bookingStore.destinations"
        optionLabel="name"
        optionValue="id"
        placeholder="Search Destinations"
        required
        :showRequired="false"
        :multiple="false" />

      <FieldAddPatients
        class="min-w-[240px]"
        name="patients"
        label="Patients"
        v-model="bookingStore.query.patients"
        errorPosition="absolute"
        placeholder="Add Patients" />

      <FieldLocation
        class="min-w-[240px]"
        name="your_location"
        label="Your Location"
        v-model="bookingStore.query.location"
        errorPosition="absolute"
        required
        placeholder="Enter Postcode"
        :showRequired="false" />

      <FieldDatePicker
        v-if="type === 'consultations'"
        class="min-w-[240px]"
        name="travel_dates"
        label="Travel Dates"
        errorPosition="absolute"
        selectionMode="range"
        required
        placeholder="Enter a date"
        v-model="bookingStore.query.dates"
        @dateSelect="onDateSelect"
        :showRequired="false" />

      <Btn
        @click.prevent="search"
        type="submit"
        gradient
        class="self-center mt-30">
        Search Clinics
      </Btn>
    </form>

    <Dialog
      v-model:visible="infoModalVisible"
      header="What is travel consultation?"
      class="max-w-[720px] max-h-[650px]">
      <ModalConsultationInfo />
    </Dialog>
  </section>
</template>

<style scoped></style>
